export type EventsPortalState = 'live' | 'upcoming' | 'past'

export const EVENTS_PORTAL_LIST_LIMIT = 50

export interface EventsPortalListParams {
  readonly state: EventsPortalState
  readonly page?: number
  readonly limit?: number
}

export interface EventsPortalPlace {
  readonly type?: string
  readonly venue?: string
  readonly address?: string
  readonly city?: string
  readonly state?: string
  readonly zip?: string
  readonly country?: string
  /** One-line address; only the account ticket view reads it. */
  readonly formattedAddress?: string
  /** Maps deep link for the venue; the account ticket view and the event about location read it. */
  readonly googleLocation?: string
}

export interface EventsPortalEvent {
  readonly id: string
  readonly eventName: string
  readonly summary?: string
  readonly coverImage?: string
  readonly startDateTime?: string
  readonly endDateTime?: string
  readonly timezone?: string
  readonly place?: EventsPortalPlace
  /** The viewer's roles in this event; any viewer role means the viewer is registered. */
  readonly roles?: readonly string[]
}

export interface EventsPortalStatePagination {
  readonly page: number
  readonly limit: number
  readonly total: number
  readonly hasMore: boolean
}

export interface EventsPortalStatePage {
  readonly events: readonly EventsPortalEvent[]
  readonly pagination: EventsPortalStatePagination
}

/** Page params shared by the viewer's my-events lists. */
export interface EventsMyEventsListParams {
  readonly page?: number
  readonly limit?: number
}

export interface EventsMyEventLocation {
  readonly venue?: string
  readonly formattedAddress?: string
}

/** The event summary every my-events row carries. */
export interface EventsMyEventSummary {
  readonly eventId: string
  readonly name?: string
  readonly imageUrl?: string
  readonly startDateTime?: string
  readonly endDateTime?: string
  readonly timeZone?: string
  readonly state?: string
  readonly location?: EventsMyEventLocation
}

export interface EventsMyEventTicketItem {
  readonly event: EventsMyEventSummary
  readonly ticketCount?: number
}

export interface EventsMyEventScheduleSession {
  readonly sessionId: string
  readonly title?: string
  readonly type?: string
  /** Calendar day in the event time zone, `YYYY-MM-DD`. */
  readonly day?: string
  /** Wall-clock `HH:mm` in the event time zone. */
  readonly startDateTime?: string
  readonly endDateTime?: string
  readonly venue?: string
  readonly description?: string
}

export interface EventsMyEventScheduleItem {
  readonly event: EventsMyEventSummary
  readonly sessions?: readonly EventsMyEventScheduleSession[]
}

export interface EventsMyEventConnectionPerson {
  readonly fullName?: string
  readonly title?: string
  readonly company?: string
  readonly avatar?: string
  readonly portalUserId?: string
}

export interface EventsMyEventConnectionItem {
  readonly connectionId: string
  readonly conversationId?: string
  readonly person?: EventsMyEventConnectionPerson
}

/** Response from GET /internal/portal/my-events/{tickets,schedule,connections} */
export interface EventsMyEventsListApiResponse<TItem> {
  readonly items?: readonly TItem[]
  readonly pagination?: Partial<EventsPortalStatePagination>
}

export interface EventsMyEventsListResponse<TItem> {
  readonly items: readonly TItem[]
  readonly pagination: EventsPortalStatePagination
}

export interface EventsMyEventTicketAttendeesParams {
  readonly eventId: string
  readonly page?: number
  readonly limit?: number
}

/** One ticket the viewer holds for an event. */
export interface EventsMyEventTicketAttendee {
  readonly id: string
  readonly ticketId?: string
  readonly orderId?: string
  readonly qrLink?: string
  readonly deleted?: boolean
}

export interface EventsMyEventTicketType {
  readonly name?: string
}

export interface EventsMyEventTicketOrder {
  readonly buyerPdfLink?: string
}

export interface EventsMyEventTicketAttendeesMeta {
  readonly tickets?: Readonly<Record<string, EventsMyEventTicketType>>
  readonly orders?: Readonly<Record<string, EventsMyEventTicketOrder>>
}

/** Response from GET /internal/portal/my-events/:eventId/tickets */
export interface EventsMyEventTicketAttendeesApiResponse {
  readonly attendees?: readonly EventsMyEventTicketAttendee[]
  readonly meta?: EventsMyEventTicketAttendeesMeta
  readonly pagination?: Partial<EventsPortalStatePagination>
}

export interface EventsMyEventTicketAttendeesResponse {
  readonly attendees: readonly EventsMyEventTicketAttendee[]
  readonly meta: Required<EventsMyEventTicketAttendeesMeta>
  readonly pagination: EventsPortalStatePagination
}

export interface EventsPortalEventsBucket {
  events?: EventsPortalEvent[]
  pagination?: Partial<EventsPortalStatePagination>
}

/** Response from GET /internal/portal/events — `events` is either the requested bucket's rows or every bucket keyed by state. */
export interface EventsPortalEventsResponse {
  events?: EventsPortalEvent[] | Partial<Record<EventsPortalState, EventsPortalEventsBucket>>
  pagination?: Partial<EventsPortalStatePagination>
}

export interface EventsManagementPageSection {
  readonly key: string
  readonly hidden?: boolean
}

export interface EventsManagementOrganizer {
  readonly name?: string
  readonly email?: string
  readonly phone?: string
  readonly website?: string
  readonly imageUrl?: string
}

/** Where a custom Attendee App section's content comes from. */
export interface EventsManagementAttendeeAppSectionSource {
  readonly type?: string
  readonly url?: string
  readonly mediaType?: string
  readonly mimeType?: string
  readonly fileName?: string
}

/** Who may see an Attendee App section: a legacy string, `{ type: 'role', role }` or `{ type: 'roles', roles }`. */
export type EventsManagementSectionAudience =
  | string
  | { readonly type?: string; readonly role?: string; readonly roles?: readonly string[] }

/** One section of the organizer-configured Attendee App, in display order. */
export interface EventsManagementAttendeeAppSection {
  readonly sectionId?: string
  readonly kind?: string
  readonly label?: string
  readonly enabled?: boolean
  readonly source?: EventsManagementAttendeeAppSectionSource
  readonly audience?: EventsManagementSectionAudience | null
}

/** The event page's call to action; the register button follows `action: 'register'`. */
export interface EventsManagementEventCta {
  readonly action?: string
  readonly url?: string
}

export interface EventsManagementAttendeeApp {
  readonly sections?: readonly EventsManagementAttendeeAppSection[]
}

/** One joinable stream of an online event; `joinUrl` stays null until the organizer opens it. */
export interface EventsManagementOnlineSession {
  readonly id: string
  readonly title?: string | null
  readonly description?: string | null
  readonly imageUrl?: string | null
  readonly status?: string | null
  readonly joinUrl?: string | null
  /** When the join link opens, if the API knows it. */
  readonly availableAt?: string | null
}

export interface EventsManagementEvent {
  readonly id: string
  readonly name: string
  readonly summary?: string
  readonly description?: string
  readonly coverImage?: string
  readonly startDateTime?: string
  readonly endDateTime?: string
  readonly timezone?: string
  readonly displayFormat?: '12h' | '24h'
  readonly location?: EventsPortalPlace
  readonly organizer?: EventsManagementOrganizer
  readonly eventUrl?: string
  readonly pageSections?: readonly EventsManagementPageSection[]
  /** The viewer's participant roles in this event, resolved server-side; any viewer role means registered. */
  readonly roles?: readonly string[]
  readonly cta?: EventsManagementEventCta
  readonly attendeeApp?: EventsManagementAttendeeApp
  /** `upcoming` / `live` / `past`; the account ticket view and the event detail agenda read it. */
  readonly eventState?: string
  /** Online events only; the account ticket view joins through the first session. */
  readonly onlineSessions?: readonly EventsManagementOnlineSession[]
}

/** Response from GET /internal/portal/events/:eventId */
export interface EventsManagementDetailResponse {
  readonly event?: EventsManagementEvent | null
}

export interface EventsManagementLink {
  readonly title?: string
  readonly url?: string
}

export interface EventsManagementSpeakerLocation {
  readonly city?: string
  readonly region?: string
  readonly state?: string
}

export interface EventsManagementSpeaker {
  readonly id: string
  readonly name: string
  readonly imageUrl?: string
  readonly tagline?: string
  readonly title?: string
  readonly jobTitle?: string
  readonly designation?: string
  readonly company?: string
  readonly organization?: string
  readonly companyName?: string
  readonly label?: string | readonly string[]
  readonly tag?: string | readonly string[]
  readonly tags?: readonly string[]
  readonly location?: string | EventsManagementSpeakerLocation
  readonly city?: string
  readonly region?: string
  readonly state?: string
  readonly description?: string
  readonly bio?: string
  readonly sortOrder?: number
  readonly socialLinks?: { readonly customLinks?: readonly EventsManagementLink[] }
  /** Opens the speaker's user profile; a speaker without a portal account has none. */
  readonly clientPortalUserId?: string
}

/** Response from GET /internal/portal/my-events/:eventId/me: the viewer's own participant on the event. */
export interface EventsManagementMyParticipantResponse {
  readonly eventId?: string
  readonly participantId?: string
  readonly role?: string
  readonly displayName?: string
  readonly initials?: string
  readonly avatar?: string
  readonly title?: string
  readonly company?: string
  readonly slug?: string
}

/** Response from GET /public/events/:eventId/speakers */
export interface EventsManagementSpeakersResponse {
  readonly speakers?: readonly EventsManagementSpeaker[]
}

export interface EventsManagementSession {
  readonly id: string
  readonly title: string
  /** Calendar day in the event time zone, `YYYY-MM-DD`. */
  readonly day?: string
  /** Wall-clock `HH:mm` in the event time zone. */
  readonly startDateTime?: string
  readonly endDateTime?: string
  readonly durationMinutes?: number
  readonly stage?: string
  readonly label?: string
  readonly description?: string
  readonly sortOrder?: number
  readonly saved?: boolean
  /** Older payloads send `isSaved`; the optimistic update writes `saved`. */
  readonly isSaved?: boolean
  readonly speakers?: readonly EventsManagementSpeaker[]
  /** Ticket types that include the session; absent when every ticket does. */
  readonly ticketAccess?: { readonly ticketNames?: readonly string[] }
}

/** Response from GET /public/events/:eventId/schedule */
export interface EventsManagementScheduleApiResponse {
  readonly sessions?: readonly EventsManagementSession[]
  readonly settings?: { readonly timezone?: string }
}

export interface EventsManagementScheduleResponse {
  readonly sessions: readonly EventsManagementSession[]
  readonly timezone?: string
}

export interface EventsManagementSponsor {
  readonly id: string
  readonly name?: string
  readonly logo?: EventsManagementImage
  readonly redirection?: EventsManagementRedirection
}

export interface EventsManagementSponsorTier {
  readonly id: string
  readonly title?: string
  readonly sortOrder?: number
  readonly sponsors?: readonly EventsManagementSponsor[]
}

/** Response from GET /public/events/:eventId/sponsors */
export interface EventsManagementSponsorsResponse {
  readonly sponsorTiers: readonly EventsManagementSponsorTier[]
}

/** Built-in ad placements. `about` is the overview's side-column ad; the others are the banner above their tab. */
export const EVENT_AD_PLACEMENTS = ['about', 'agenda', 'speakers', 'attendees', 'exhibitors'] as const

/** The placement shown as the overview's side-column card rather than the banner above the tab. */
export const EVENT_SIDE_AD_PLACEMENT = 'about'

export interface EventsManagementAdvertisementsParams {
  readonly eventId: string
  /** A built-in placement, or a custom Attendee App section id. */
  readonly placement: string
}

export interface EventsManagementImage {
  readonly url?: string
}

/**
 * Where a click on a creative or logo goes: an `external_url` opens `url` in a new tab, an `exhibitor` opens that
 * exhibitor's profile on the event page. Anything else (`none`) is not actionable.
 */
export interface EventsManagementRedirection {
  readonly type?: string
  readonly url?: string
  readonly exhibitorId?: string
}

export interface EventsManagementAdvertisement {
  readonly id: string
  readonly webImage?: EventsManagementImage
  readonly mobileImage?: EventsManagementImage
  readonly altText?: string
  readonly redirection?: EventsManagementRedirection
}

/** Response from GET /public/events/:eventId/advertisements */
export interface EventsManagementAdvertisementsResponse {
  readonly advertisements: readonly EventsManagementAdvertisement[]
}

export type EventsManagementAdInteractionType = 'view' | 'click'

/** Body for POST /internal/portal/events/:eventId/advertisements/:advertisementId/interactions */
export interface EventsManagementAdInteractionParams {
  readonly eventId: string
  readonly advertisementId: string
  readonly placement: string
  readonly type: EventsManagementAdInteractionType
}

export const EVENTS_ATTENDEES_PAGE_LIMIT = 10
export const EVENTS_ATTENDEES_SEARCH_DEBOUNCE_MS = 300

export interface EventsManagementAttendee {
  readonly participantId: string
  readonly displayName: string
  readonly title?: string
  readonly company?: string
  readonly role?: string
  /** Community profile slug; recipients are resolved server-side from it. */
  readonly slug?: string
  readonly imageUrl?: string
  readonly avatar?: string
  readonly isSelf?: boolean
  /** `visible` or `hidden` in the attendee list; sent only on the viewer's own row. */
  readonly visibility?: string
  /** The attendee's portal account; the user profile is keyed by it. */
  readonly clientPortalUserId?: string
}

export interface EventsManagementAttendeesPagination {
  readonly total: number
  readonly page: number
  readonly limit: number
  readonly hasNext: boolean
}

export interface EventsManagementAttendeesParams {
  readonly page?: number
  readonly limit?: number
  readonly search?: string
}

/** The viewer hides or shows themselves in one event's attendee list; the person comes from the session, never the body. */
export interface EventsManagementAttendeeVisibilityUpdateParams {
  readonly eventId: string
  readonly visibility: 'visible' | 'hidden'
}

/** Response from GET /internal/portal/my-events/:eventId/attendees */
export interface EventsManagementAttendeesApiResponse {
  readonly items?: readonly EventsManagementAttendee[]
  readonly pagination?: Partial<EventsManagementAttendeesPagination>
}

export interface EventsManagementAttendeesResponse {
  readonly items: readonly EventsManagementAttendee[]
  readonly pagination: EventsManagementAttendeesPagination
}

/** Participants per connection-actions request; the portal rejects larger batches. */
export const EVENTS_CONNECTION_PARTICIPANTS_LIMIT = 100
/** Connection ids per status request; the portal rejects larger batches. */
export const EVENTS_CONNECTION_STATUSES_LIMIT = 50
export const EVENTS_CONNECTION_MESSAGE_MAX_LENGTH = 200

/** Exhibitor team members connect and hold event roles as `exhibitor_member`. */
export type EventParticipantRole = 'attendee' | 'speaker' | 'exhibitor_member'

export interface EventParticipantReference {
  readonly role: EventParticipantRole
  readonly participantId: string
}

/**
 * The person a profile link opens. A portal account id is sent on its own; without one, the participant id is
 * sent with its role and the people endpoint resolves the person from it.
 */
export interface EventUserProfileTarget {
  readonly id: string
  readonly role?: EventParticipantRole
}

export type EventConnectionAction =
  | { readonly state: 'self' | 'unavailable' | 'connect' }
  | { readonly state: 'outgoing_pending'; readonly connectionId: string; readonly requestVersion: number }
  | {
      readonly state: 'incoming_pending'
      readonly connectionId: string
      readonly requestVersion: number
      readonly introMessage: string
    }
  | {
      readonly state: 'message'
      readonly connectionId: string
      readonly requestVersion: number
      readonly conversationId: string
    }

export type EventParticipantConnectionAction = EventConnectionAction & {
  readonly participant: EventParticipantReference
}

export interface EventConnectionQueryParams {
  readonly eventId: string
  readonly participants: readonly EventParticipantReference[]
}

export interface RequestEventConnectionParams {
  readonly eventId: string
  readonly participant: EventParticipantReference
  readonly message: string
}

export type RequestEventConnectionResponse = Exclude<EventConnectionAction, { state: 'self' | 'unavailable' | 'connect' }> & {
  readonly created?: boolean
}

export interface DecideEventConnectionParams {
  readonly connectionId: string
  readonly requestVersion: number
}

export interface EventConnectionStatus {
  readonly connectionId: string
  readonly status: 'pending' | 'accepted' | 'declined'
  readonly direction: 'incoming' | 'outgoing'
  readonly requestVersion: number
  readonly conversationId?: string
}

export interface AcceptEventConnectionResponse {
  readonly state: 'message'
  readonly connectionId: string
  readonly requestVersion: number
  readonly conversationId: string
}

export interface DeclineEventConnectionResponse {
  readonly state: 'declined'
  readonly connectionId: string
  readonly requestVersion: number
}

export interface EventsManagementSavedSessionParams {
  readonly eventId: string
  readonly sessionId: string
  readonly saved: boolean
}

export const EVENTS_SAVED_SESSIONS_PAGE_LIMIT = 50

export interface EventsManagementSavedSessionsParams {
  readonly page?: number
  readonly limit?: number
}

export interface EventsManagementSavedScheduleSession {
  readonly sessionId: string
  readonly saved?: boolean
}

export interface EventsManagementSavedScheduleItem {
  /** The event the sessions belong to, as the viewer schedule returns it. */
  readonly event?: { readonly eventId?: string } | null
  readonly sessions: readonly EventsManagementSavedScheduleSession[]
}

/** Response from GET /internal/portal/my-events/schedule — the viewer's saved sessions, grouped per event. */
export interface EventsManagementSavedScheduleApiResponse {
  readonly items?: readonly (Omit<EventsManagementSavedScheduleItem, 'sessions'> & {
    readonly sessions?: readonly EventsManagementSavedScheduleSession[]
  })[]
  readonly pagination?: Partial<EventsPortalStatePagination>
}

export interface EventsManagementSavedSessionsResponse {
  readonly items: readonly EventsManagementSavedScheduleItem[]
  readonly pagination: EventsPortalStatePagination
}

/** A member of the exhibitor's team. */
export interface EventsManagementExhibitorMember {
  readonly id: string
  readonly name: string
  readonly jobTitle?: string
  readonly email?: string
  /** Booth permission from the exhibitor detail API. Anything but `admin` is treated as `member`. */
  readonly boothRole?: string
  readonly imageUrl?: string
  /** The member's portal account; the user profile is keyed by it. */
  readonly clientPortalUserId?: string
  /** True on the viewer's own row. */
  readonly isSelf?: boolean
  /** `visible` or `hidden` on the public exhibitor profile. */
  readonly visibility?: string
  /** Whether the contacts this member collects are shared with the exhibitor's team. */
  readonly shareConnections?: boolean
}

export interface EventsManagementExhibitor {
  readonly id: string
  readonly name: string
  readonly tierId?: string
  /** Server-computed tier position; falls back to the tier's `order`. */
  readonly tierRank?: number
  readonly description?: string
  readonly websiteUrl?: string
  readonly logoUrl?: string
  readonly headerImageUrl?: string
  readonly contactEmail?: string
  /** Not rendered by the portal; read only so the exhibitor PUT can carry it forward. */
  readonly contactPhone?: string
  /** Not rendered by the portal; read only so the exhibitor PUT can carry it forward. */
  readonly address?: EventsManagementExhibitorAddress
  readonly booth?: { readonly number?: string; readonly note?: string }
  readonly socialLinks?: { readonly customLinks?: readonly EventsManagementLink[] }
  readonly sortOrder?: number
  readonly bookmarked?: boolean
  readonly showAboutAndLinks?: boolean
  /**
   * Whether the organizer lets this booth's admins edit its description. Only the single-exhibitor
   * endpoint returns it, and only to a booth admin; absent reads as editable.
   */
  readonly canExhibitorEditDescription?: boolean
  /** Not rendered by the portal; read only so the exhibitor PUT can carry it forward. */
  readonly showContactButton?: boolean
  /** Not rendered by the portal; read only so the exhibitor PUT can carry it forward. */
  readonly showVenueMapButton?: boolean
  /** Only the single-exhibitor endpoint returns the team. */
  readonly members?: readonly EventsManagementExhibitorMember[]
}

export interface EventsManagementExhibitorTier {
  readonly id: string
  readonly name?: string
  /** Authored tier color; only a hex literal is rendered. */
  readonly color?: string
  readonly order?: number
}

/** Response from GET /internal/portal/events/:eventId/exhibitors — every exhibitor in one response, no paging. */
export interface EventsManagementExhibitorsApiResponse {
  readonly exhibitors?: readonly EventsManagementExhibitor[]
  readonly tiers?: readonly EventsManagementExhibitorTier[]
}

export interface EventsManagementExhibitorsResponse {
  readonly exhibitors: readonly EventsManagementExhibitor[]
  readonly tiers: readonly EventsManagementExhibitorTier[]
}

export interface EventsManagementUserParams {
  readonly eventId: string
  /** The portal account id, or the participant id when `role` is set. */
  readonly id: string
  readonly role?: EventParticipantRole
}

/** A booth the person staffs, with the booth directory item's names. */
export interface EventsManagementProfileBooth {
  readonly id: string
  readonly name: string
  readonly logoUrl?: string
  readonly booth?: { readonly number?: string; readonly note?: string }
  readonly bookmarked?: boolean
}

/** One person at an event, whichever roles they hold there. */
export interface EventsManagementUser {
  /** Missing when the person was looked up by participant id and has no portal account. */
  readonly clientPortalUserId?: string
  readonly name?: string | null
  readonly title?: string | null
  readonly company?: string | null
  readonly avatarUrl?: string | null
  /** The speaker label the organiser gave the person. */
  readonly label?: string | null
  readonly bio?: string | null
  readonly socials?: { readonly customLinks?: readonly EventsManagementLink[] | null } | null
  readonly roles?: readonly string[]
  readonly isSelf?: boolean
}

/** A session on a user profile; `inMySchedule` says whether the viewer has saved it. */
export interface EventsManagementProfileSession extends EventsManagementSession {
  readonly inMySchedule?: boolean
}

/** Response from GET /internal/portal/events/:eventId/people/:id (`?role=` when `id` is a participant id) */
export interface EventsManagementUserProfileApiResponse {
  readonly person?: EventsManagementUser | null
  /** The participant a new connection request is addressed to. */
  readonly participant?: EventParticipantReference | null
  /** The viewer's connection action towards this person, in the directory's shape. */
  readonly connection?: EventConnectionAction | null
  readonly memberOf?: readonly EventsManagementProfileBooth[] | null
  /** Sessions the person speaks at. */
  readonly speakingAt?: readonly EventsManagementProfileSession[] | null
  /** Sessions the person is attending. */
  readonly attending?: readonly EventsManagementProfileSession[] | null
}

export interface EventsManagementUserProfileResponse {
  readonly person?: EventsManagementUser
  readonly participant?: EventParticipantReference
  readonly connection?: EventConnectionAction
  readonly memberOf: readonly EventsManagementProfileBooth[]
  /** Saved state is carried as `saved`, the way the schedule carries it. */
  readonly speakingAt: readonly EventsManagementSession[]
  readonly attending: readonly EventsManagementSession[]
}

export interface EventsManagementExhibitorParams {
  readonly eventId: string
  readonly exhibitorId: string
}

/** Response from GET /internal/portal/events/:eventId/exhibitors/:exhibitorId */
export interface EventsManagementExhibitorApiResponse {
  readonly exhibitor?: EventsManagementExhibitor
  /** Some responses carry the team at the root instead of on the exhibitor. */
  readonly members?: readonly EventsManagementExhibitorMember[]
  readonly tiers?: readonly EventsManagementExhibitorTier[]
}

export interface EventsManagementExhibitorResponse {
  readonly exhibitor?: EventsManagementExhibitor
  readonly tiers: readonly EventsManagementExhibitorTier[]
}

export interface EventsManagementExhibitorAddress {
  readonly street?: string
  readonly city?: string
  readonly state?: string
  readonly postalCode?: string
  readonly country?: string
}

/** Fields the portal exhibitor PUT accepts. Untouched values are copied from the cached exhibitor. */
export interface EventsManagementExhibitorUpdateBody {
  readonly name?: string
  readonly description?: string
  readonly contactEmail?: string
  readonly contactPhone?: string
  readonly websiteUrl?: string
  readonly address?: EventsManagementExhibitorAddress
  readonly logoUrl?: string
  readonly headerImageUrl?: string
  readonly socialLinks?: { readonly customLinks?: readonly EventsManagementLink[] }
  readonly showAboutAndLinks?: boolean
  readonly showContactButton?: boolean
  readonly showVenueMapButton?: boolean
}

export interface EventsManagementExhibitorUpdateParams {
  readonly eventId: string
  readonly exhibitorId: string
  readonly body: EventsManagementExhibitorUpdateBody
}

/** Only the fields being changed are sent; the API leaves the rest as they are. */
export interface EventsManagementExhibitorMemberUpdateBody {
  readonly visibility?: 'visible' | 'hidden'
  readonly shareConnections?: boolean
}

export interface EventsManagementExhibitorMemberUpdateParams {
  readonly eventId: string
  readonly exhibitorId: string
  readonly memberId: string
  readonly body: EventsManagementExhibitorMemberUpdateBody
}

export interface EventsManagementExhibitorBookmarkParams {
  readonly eventId: string
  readonly exhibitorId: string
  readonly bookmarked: boolean
}

export interface EventsManagementBookmarkExhibitor {
  readonly id: string
  readonly name?: string
  readonly logoUrl?: string
  readonly booth?: { readonly number?: string; readonly note?: string }
  readonly bookmarked?: boolean
}

export interface EventsManagementEventBookmarkSummary {
  readonly eventId?: string
  readonly name?: string
  readonly imageUrl?: string
  readonly startDateTime?: string
  readonly endDateTime?: string
  readonly timeZone?: string
  readonly state?: string
  readonly location?: { readonly venue?: string }
}

/** Response from GET /internal/portal/events/:eventId/bookmarks */
export interface EventsManagementEventBookmarksApiResponse {
  readonly event?: EventsManagementEventBookmarkSummary
  readonly exhibitors?: readonly EventsManagementBookmarkExhibitor[]
}

export interface EventsManagementEventBookmarksResponse {
  readonly event: EventsManagementEventBookmarkSummary | null
  readonly exhibitors: readonly EventsManagementBookmarkExhibitor[]
}

export interface EventsManagementExhibitorMembershipsParams {
  readonly eventId: string
}

/** One exhibitor the viewer belongs to. */
export interface EventsManagementExhibitorMembership {
  readonly exhibitorId: string
  readonly exhibitorName?: string
  /** Anything but `admin` is treated as `member`. */
  readonly memberRole?: string
  readonly logoUrl?: string
}

/** Response from GET /internal/portal/events/:eventId/exhibitor-memberships */
export interface EventsManagementExhibitorMembershipsApiResponse {
  readonly isExhibitorMember?: boolean
  readonly memberships?: readonly EventsManagementExhibitorMembership[]
}

export interface EventsManagementExhibitorMembershipsResponse {
  readonly memberships: readonly EventsManagementExhibitorMembership[]
}

export const EVENTS_LEAD_CONTACTS_PAGE_LIMIT = 10
/** The contacts endpoint rejects a longer search; the composable clips to it before a request is made. */
export const EVENTS_LEAD_CONTACTS_SEARCH_MAX_LENGTH = 100

/** The contacts endpoint sorts by connection time only; `desc` is newest first. */
export type EventsLeadContactsOrder = 'asc' | 'desc'

/** What the viewer asked the contacts list for; `page` is ignored by the infinite list. */
export interface EventsLeadContactsQuery {
  readonly search: string
  readonly order: EventsLeadContactsOrder
  readonly page: number
}

export interface EventsManagementLeadContactsParams {
  readonly eventId: string
  readonly exhibitorId: string
  readonly page: number
  readonly pageLimit: number
  readonly search?: string
  readonly order: EventsLeadContactsOrder
}

/** A team member who collected the contact. */
export interface EventsManagementLeadCollector {
  readonly memberId?: string
  readonly contactId: string
  readonly clientPortalUserId: string
  readonly name?: string
  readonly email?: string
  readonly connectedAt: string
}

/** A contact the exhibitor's team collected at the event. */
export interface EventsManagementLeadContact {
  readonly contactId: string
  readonly name?: string
  readonly email?: string
  readonly connectedAt: string
  readonly connectedWith: readonly EventsManagementLeadCollector[]
}

/** Response from GET /internal/portal/events/:eventId/exhibitors/:exhibitorId/contacts */
export interface EventsManagementLeadContactsApiResponse {
  readonly contacts?: readonly (Omit<EventsManagementLeadContact, 'connectedWith'> & {
    readonly connectedWith?: readonly EventsManagementLeadCollector[]
  })[]
  readonly pagination?: Partial<EventsPortalStatePagination>
}

export interface EventsManagementLeadContactsResponse {
  readonly contacts: readonly EventsManagementLeadContact[]
  readonly pagination: EventsPortalStatePagination
}

export interface EventsManagementLeadExportParams {
  readonly eventId: string
  readonly exhibitorId: string
}

/** The CSV GET .../exhibitors/:exhibitorId/leads/export returns, with the name the server gave it when it sent one. */
export interface EventsManagementLeadExportFile {
  readonly blob: Blob
  readonly fileName?: string
}
